import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import WebRTCIssueDetector, { IssueReason } from 'webrtc-issue-detector';
import { useTranslation } from 'react-i18next';
import usePeerNetworkStatusListener from '../hooks/usePeerNetworkStatusListener';
import { useDispatch, useSelector } from 'react-redux';
import { createSnack } from '../actions/SnackActions';
import { track } from '../actions/TrackActions';
import logger from '../utils/logger';

const NETWORK_ERROR_LIMIT = 5;
const CPU_ERROR_LIMIT = 3;
const MAX_ELAPSED_TIME = 30000;

const init = {
  webRtcIssuesDetector: {
    network: 0,
    cpu: 0,
  },
  setWebRtcIssuesDetector: () => {},
  networkStatuses: {},
  setNetworkStatuses: () => {},
  reportNetworkStatus: () => {},
};

export const WebRTCIssueStatusContext = createContext(init);

export const useWebRTCStatus = () => useContext(WebRTCIssueStatusContext);

export const WebRTCIssueStatusProvider = ({ children, roomId, roomClient }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.token);
  const videoHidden = useSelector((state) => state.control.hideVideo);
  const networkStatuses = useSelector(
    (state) => state.monitoring.networkStatuses
  );
  const { t } = useTranslation();
  const { reportNetworkStatus } = usePeerNetworkStatusListener(
    roomId,
    roomClient
  );
  const [, setNetworkIssuesIndex] = useState(0);
  const [networkIssuesTimestamps, setNetworkIssuesTimestamps] = useState(
    Array(NETWORK_ERROR_LIMIT).fill(0)
  );
  const [, setCpuIssuesIndex] = useState(0);
  const [cpuIssuesTimestamps, setCpuIssuesTimestamps] = useState(
    Array(CPU_ERROR_LIMIT).fill(0)
  );

  const [, setLastVideoBytesSent] = useState([]);
  const [isRunningOnLowestLayer, setIsRunningOnLowestLayer] = useState(false);

  useEffect(() => {
    const webRtcIssueDetector = new WebRTCIssueDetector({
      onIssues: (issues) => {
        let recordedNetwork = false;
        issues.forEach((issue) => {
          logger.log('GOT WEBRTC ISSUE', issue);
          logger.log('Issues type:', issue.type); // eg. "network"
          logger.log('Issues reason:', issue.reason); // eg. "outbound-network-throughput"
          logger.log('Issues reason:', issue.statsSample); // eg. "packetLossPct: 12%, avgJitter: 230, rtt: 150"

          const { type, reason } = issue;
          if (
            type === 'network' &&
            !recordedNetwork &&
            (reason === IssueReason.OutboundNetworkQuality ||
              reason === IssueReason.OutboundNetworkThroughput)
          ) {
            recordNetworkIssue();
            recordedNetwork = true;
          } else if (type === 'cpu') {
            recordCpuIssue();
          }

          track('webrtc-issue', { issue }, token);
        });
      },
    });

    webRtcIssueDetector.watchNewPeerConnections();
    return () => {
      webRtcIssueDetector.stopWatchingNewPeerConnections();
    };
  }, [token]);

  useEffect(() => {
    const t = setInterval(async () => {
      const producer = roomClient.videoProducer;
      if (!producer || producer.closed) return;
      const stats = Array.from((await producer.getStats()).values());
      const videoStats = stats.filter(
        (stat) => stat.kind === 'video' && stat.type === 'outbound-rtp'
      );
      let _lastSent;
      setLastVideoBytesSent((videoBytesSentHistory) => {
        _lastSent = videoBytesSentHistory;
        return videoStats;
      });
      if (!_lastSent) return;

      const lastSentPerFrameHeight = _lastSent.reduce((acc, stat) => {
        const frameHeight = stat.frameHeight;
        if (!stat.frameHeight) return acc;
        acc[frameHeight] = stat.bytesSent;
        return acc;
      }, {});

      const maxSpatialLayerThatShouldBeSent = videoStats.filter(
        (x) => x.active
      ).length;
      // Filter for layers that have bytesSent > 0, frameHeight set and a byteSent > last byteSent
      const maxSpatialLayerSent = videoStats.filter(
        (x) =>
          x.bytesSent > 0 &&
          x.frameHeight &&
          x.bytesSent > lastSentPerFrameHeight[x.frameHeight]
      ).length;

      if (maxSpatialLayerThatShouldBeSent > 1 && maxSpatialLayerSent <= 1) {
        // We are running on lowest layer
        setIsRunningOnLowestLayer(true);
      } else {
        setIsRunningOnLowestLayer(false);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [roomClient.videoProducer]);

  function recordNetworkIssue() {
    let _currIndex;
    setNetworkIssuesIndex((networkIssuesIndex) => {
      _currIndex = (networkIssuesIndex + 1) % NETWORK_ERROR_LIMIT;
      return _currIndex;
    });
    setNetworkIssuesTimestamps((networkIssuesTimestamps) =>
      networkIssuesTimestamps.map((item, index) =>
        index === _currIndex ? new Date() : item
      )
    );
  }

  function recordCpuIssue() {
    let _currIndex;
    setCpuIssuesIndex((cpuIssuesIndex) => {
      _currIndex = (cpuIssuesIndex + 1) % CPU_ERROR_LIMIT;
      return _currIndex;
    });
    setCpuIssuesTimestamps((cpuIssuesTimestamps) =>
      cpuIssuesTimestamps.map((item, index) =>
        index === _currIndex ? new Date() : item
      )
    );
  }

  const reportNetworkIssue = useCallback(() => {
    let msg = t('Your network connection is shaky');
    let options = {};
    if (
      isRunningOnLowestLayer &&
      !roomClient.videoProducer?.closed &&
      !videoHidden
    ) {
      options = {
        customSnack: 'bad-network',
        waitTime: 20000,
      };
      msg = t(
        'Your network connection is shaky. We recommend you turn off your video to improve audio quality.'
      );
    }
    createSnack(dispatch, msg, 'warning', options);
    reportNetworkStatus(1);
    setNetworkIssuesIndex(0);
    setNetworkIssuesTimestamps(Array(NETWORK_ERROR_LIMIT).fill(0));
  }, [
    isRunningOnLowestLayer,
    roomClient.videoProducer,
    videoHidden,
    dispatch,
    reportNetworkStatus,
    t,
  ]);

  const reportCpuIssue = useCallback(() => {
    createSnack(dispatch, t('Your CPU is struggling'), 'warning');
    setCpuIssuesIndex(0);
    setCpuIssuesTimestamps(Array(CPU_ERROR_LIMIT).fill(0));
  }, [dispatch, t]);

  useEffect(() => {
    const now = new Date();
    const showNotification = networkIssuesTimestamps.every(
      (val) => now - val <= MAX_ELAPSED_TIME
    );
    if (showNotification) {
      reportNetworkIssue();
    }
  }, [networkIssuesTimestamps, reportNetworkIssue]);

  useEffect(() => {
    const now = new Date();
    const showNotification = cpuIssuesTimestamps.every(
      (val) => now - val <= MAX_ELAPSED_TIME
    );
    if (showNotification) {
      reportCpuIssue();
    }
  }, [cpuIssuesTimestamps, reportCpuIssue]);

  const value = {
    networkStatuses,
    reportNetworkStatus,
  };
  return (
    <WebRTCIssueStatusContext.Provider value={value}>
      {children}
    </WebRTCIssueStatusContext.Provider>
  );
};
